import { getPhoneCom } from 'phonecom'
import { findPhoneNumbersInText } from 'libphonenumber-js'
import { getOtherUseCaseTextValue } from './util'
import generateCampaignDescription from './description-generator'

/*
 This is documentation for these payloads:
 https://docs.google.com/document/d/1sgnbuSRERxTCoWOpKEA1DQKOkibPTzQcVmnTLzKTyng/edit?tab=t.0
*/

/**
 *
 */
const getAddBrandParams = async (wizardData, additionalParams = {}) => {
    const phonecom = await getPhoneCom()

    return {
        business_info: {
            legalBizName: wizardData.business_info.legalBizName,
            registrationCountry: wizardData.business_info.registrationCountry,
            federalBusinessID: wizardData.business_info.federalBusinessID,
            selectedBizType: wizardData.business_info.selectedBizType,
            selectedVerticle: wizardData.business_info.selectedVerticle,
            stockSymbol: wizardData.business_info.stockSymbol,
            brandName: wizardData.business_info.brandName
        },
        business_address: {
            address: wizardData.business_address.address,
            address_2: wizardData.business_address.address_2,
            state: wizardData.business_address.state,
            city: wizardData.business_address.city,
            postalCode: wizardData.business_address.postalCode,
            primaryBusinessPhone: wizardData.business_address.primaryBusinessPhone,
            supportPhone: wizardData.business_address.supportPhone,
            bizWebsite: wizardData.business_address.bizWebsite,
            supportEmail: wizardData.business_address.supportEmail
        },
        business_confirm: true,
        account_id: phonecom.voip_id,
        mock: wizardData.business_address.mock,
        ...additionalParams
    }
}

/**
 *
 */
const getEditBrandParams = async (wizardData, editing, additionalParams = {}) => {
    // payment-failed goes directly to the last step
    // so we dont have data before that
    let data = {
        csp_brand_id: editing.csp_brand_id
        // identifier:
        // business_identifier:
        // registration_status:
        // deleted_at:
    }
    if (wizardData?.business_address) {
        data = {
            ...data,
            mobile_phone: wizardData.business_address.primaryBusinessPhone,
            company: wizardData.business_address.bizWebsite,
            address: wizardData.business_address.address,
            address_2: wizardData.business_address.address_2,
            city: wizardData.business_address.city,
            state: wizardData.business_address.state,
            zip: wizardData.business_address.postalCode,
            is_mock: wizardData.business_address.mock,
            phone: wizardData.business_address.supportPhone,
            // fax:
            email: wizardData.business_address.supportEmail
            // email_2:
            // valid:
            // standardized:
            // format:
            // info_id:
            // type:
            // linked_account_id:
            // created_date:
        }
    }
    if (wizardData?.business_info) {
        data = {
            ...data,
            // stock_exchange:
            stock_symbol: wizardData.business_info.stockSymbol,
            name: wizardData.business_info.brandName,
            // contact_id:
            'contact.name': wizardData.business_info.legalBizName,
            country: wizardData.business_info.registrationCountry,
            business_type_name: wizardData.business_info.selectedBizType,
            business_vertical_name: wizardData.business_info.selectedVerticle
        }
    }
    return {
        ...data,
        ...additionalParams
    }
}

const getAllSampleMessages = (campaignSmsContent): string[] => {
    if (!campaignSmsContent) {
        return []
    }

    return [
        campaignSmsContent.optInMessage,
        campaignSmsContent.optOutMessage,
        campaignSmsContent.helpMessage,
        campaignSmsContent.samplesms1?.value,
        campaignSmsContent.samplesms2?.value,
        campaignSmsContent.samplesms3?.value,
        campaignSmsContent.samplesms4?.value,
        campaignSmsContent.samplesms5?.value
    ].filter((message): message is string => Boolean(message))
}

const doesSampleMessagesContainUrl = (sampleMessages: string[] = []) => {
    const urlRegex = /(^|[^@\w.-])((https?:\/\/|www\.)[^\s<>()]+|([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s<>()]*)?)/i

    return sampleMessages.some((message) => urlRegex.test(message))
}

const doesSampleMessagesContainPhoneNumber = (sampleMessages: string[] = []) => {
    return sampleMessages.some((message) => {
        // 'US' coveres numbers like: (212) 555-0123
        return findPhoneNumbersInText(message).length || findPhoneNumbersInText(message, 'US').length
    })
}

const getEmbeddedLinkValue = (campaignSmsContent) => {
    const sampleMessagesArray = getAllSampleMessages(campaignSmsContent)
    return doesSampleMessagesContainUrl(sampleMessagesArray)
}

const getEmbeddedPhoneValue = (campaignSmsContent) => {
    const sampleMessagesArray = getAllSampleMessages(campaignSmsContent)
    return doesSampleMessagesContainPhoneNumber(sampleMessagesArray)
}

/**
 *
 */
const getAddCampaignParams = async (wizardData, additionalParams = {}) => {
    const phonecom = await getPhoneCom()
    return {
        account_id: phonecom.voip_id,
        brandId: wizardData.campaign_usage.selectedBrand.csp_brand_id,
        campaignDescription: generateCampaignDescription(wizardData.campaign_usage.useCases, wizardData.campaign_usage.selectedBrand),
        campaignName: wizardData.campaign_usage.campaignName,
        campaign_usecase: {
            scheduleAppointments: wizardData.campaign_usage.useCases.includes('SCHEDULE_APPOINTMENTS'),
            orderStatus: wizardData.campaign_usage.useCases.includes('UPDATE_ORDER_STATUS'),
            salesOffer: wizardData.campaign_usage.useCases.includes('SALE_AND_OFFERS'),
            servicePromotions: wizardData.campaign_usage.useCases.includes('SERVICE_AND_EVENTS_PROMOTION'),
            customerSupport: wizardData.campaign_usage.useCases.includes('ASSISTENCE_AND_CUSTOMER_SUPPORT'),
            accountNotification: wizardData.campaign_usage.useCases.includes('ACCOUNT_NOTIFICATION'),
            higherEducation: wizardData.campaign_usage.useCases.includes('HIGHER_EDUCATION'),
            otherUseCase: getOtherUseCaseTextValue(wizardData.campaign_usage.useCases)
        },
        campaign_consent: {
            consentInfo: wizardData.campaign_usage.consentInfo,
            privacyPolicyUrl: wizardData.campaign_usage.privacyPolicyLink
        },
        campaign_message: {
            optInKeywords: '',
            optInMessage: wizardData.campaign_sms_content.optInMessage,
            optOutKeywords: '',
            optOutMessage: wizardData.campaign_sms_content.optOutMessage,
            helpKeywords: '',
            helpMessage: wizardData.campaign_sms_content.helpMessage,
            embeddedLink: getEmbeddedLinkValue(wizardData.campaign_sms_content),
            embeddedPhone: getEmbeddedPhoneValue(wizardData.campaign_sms_content)
        },
        campaign_sms_content: {
            messageFlow: wizardData.campaign_usage.messageFlow,
            messageOne: wizardData.campaign_sms_content.samplesms1.value,
            messageTwo: wizardData.campaign_sms_content.samplesms2.value,
            messageThree: wizardData.campaign_sms_content.samplesms3.value,
            messageFour: wizardData.campaign_sms_content.samplesms4.value,
            messageFive: wizardData.campaign_sms_content.samplesms5.value
        },
        campaign_policies: {
            checked: wizardData.campaign_policies.checked,
            checked2: wizardData.campaign_policies.checked2,
            checked3: wizardData.campaign_policies.checked3
        },
        campaign_contact: {
            name: wizardData.campaign_contact.name,
            email: wizardData.campaign_contact.email
        },
        campaign_document_ids: wizardData.campaign_usage.files.map((x) => x.document_id || x.csp_document_id).filter(Boolean),
        // and now required fields
        campaign_preview: {},
        campaign_invoice: true,
        campaign_submit: {},
        ...additionalParams
    }
}

/**
 *
 */
const getEditCampaignParams = async (wizardData, editing, additionalParams = {}) => {
    const phonecom = await getPhoneCom()

    // payment-failed goes directly to the last step
    // so we dont have data before that
    let data = {
        account_id: phonecom.voip_id,
        csp_campaign_id: editing.csp_campaign_id
    }
    // identifier: '',
    // description: '',
    // deleted_at:null,
    if (wizardData?.campaign_sms_content) {
        data = {
            ...data,
            samplesms1: wizardData.campaign_sms_content.samplesms1.value,
            samplesms2: wizardData.campaign_sms_content.samplesms2.value,
            samplesms3: wizardData.campaign_sms_content.samplesms3.value,
            samplesms4: wizardData.campaign_sms_content.samplesms4.value,
            samplesms5: wizardData.campaign_sms_content.samplesms5.value,
            keywords: {
                start: {
                    // Campaignkeyword: "START",
                    CampaignkeywordValue: wizardData.campaign_sms_content.optInMessage
                },
                stop: {
                    // Campaignkeyword: "STOP",
                    CampaignkeywordValue: wizardData.campaign_sms_content.optOutMessage
                },
                help: {
                    // "Campaignkeyword": "HELP",
                    CampaignkeywordValue: wizardData.campaign_sms_content.helpMessage
                }
            },
            embeddedLink: getEmbeddedLinkValue(wizardData.campaign_sms_content),
            embeddedPhone: getEmbeddedPhoneValue(wizardData.campaign_sms_content)
        }
    }

    if (wizardData?.campaign_usage) {
        data = {
            ...data,
            consent_info: wizardData.campaign_usage.consentInfo,
            privacy_policy_url: wizardData.campaign_usage.privacyPolicyLink,
            // number_pool: false,
            // age_gated: false,
            // direct_lending: false,
            // uc_appointments: false,
            // uc_orders: false,
            // uc_sales: false,
            // uc_promotions: false,
            // uc_support: false,
            // uc_other: '',
            // status:"",
            name: wizardData.campaign_usage.campaignName,
            messageflow: wizardData.campaign_usage.messageFlow,
            // created_at: 1689692286,
            // brand_name: "Phonee.comInc",
            csp_brand_id: wizardData.campaign_usage.selectedBrand.csp_brand_id
            // is_mock: "N",
            // use_cases: wizardData.campaign_usage.useCases, // it can't be updated anyway, and ee have situation with OTHER
        }
    }

    if (wizardData?.campaign_contact) {
        data = {
            ...data,
            contact_name: wizardData.campaign_contact.name,
            contact_email: wizardData.campaign_contact.email
        }
    }

    return {
        ...data,
        ...additionalParams
    }
}

/**
 *
 */
const getDeleteSmsNumberParams = async (smsNumber) => {
    const phonecom = await getPhoneCom()
    const params = {
        account_id: phonecom.voip_id,
        csp_campaign_id: smsNumber?.csp_campaign_id,
        voip_did_ids: [smsNumber?.voip_did_id],
        delete: true
        // onSuccess: () => {
        //     fetchCampaigns(initialCampaignFetchParams)
        // }
    }

    return params
}

/**
 *
 */
const getAddSmsNumberParams = async (smsNumber) => {
    const phonecom = await getPhoneCom()
    const params = {
        account_id: phonecom.voip_id,
        csp_campaign_id: smsNumber.campaign_step.selectedCampaign.csp_campaign_id,
        voip_did_ids: smsNumber.number_step.selectedPhoneNumber.map(x => x.voip_did_id),
        delete: false
        // onSuccess: () => {
        //     fetchCampaigns(initialCampaignFetchParams)
        // }
    }
    return params
}

/**
 *
 */
export {
    getAddBrandParams,
    getEditBrandParams,
    getAddCampaignParams,
    getEditCampaignParams,
    getDeleteSmsNumberParams,
    getAddSmsNumberParams
}
